<template>
    <div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
	<div v-else class="container py-5">
        <h2>{{ $t('parameterDetail') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="'/modeling/'+ this.$route.params.id + '/assettype/' + this.$route.params.assetTypeId" >
                        {{ assetTypeName || $t('assetType') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="'/modeling/'+ this.$route.params.id + '/assettype/' + this.$route.params.assetTypeId + '/parameter/' + this.$route.params.parameterId" >
                        {{ data.name || $t('parameter') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ $t('parameterDetail') }}</li>
            </ol>
        </nav>
        <div class="row mb-3">
            <div class="col-md-3">
                <label class="form-label" for="name" required>{{ $t('name') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="name" >
            </div>
            <div class="col-md-3">
                <label class="form-label" for="description" required>{{ $t('description') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="description">
            </div>
            <div class="col-md-3">
                <label class="form-label" for="parameterAttribute" required>{{ $t('parameterAttribute') }}</label>
                <select id="parameterAttribute" class="form-select" v-model="data.parameterAttribute">
                    <option disabled>{{ $t('select') }}</option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
            <div class="col-md-3">
                <label class="form-label" for="parameterAttributeYear" required>{{ $t('parameterAttributeYear') }}</label>
                <select id="parameterAttributeYear" class="form-select" v-model="data.parameterAttributeYear">
                    <option disabled>{{ $t('select') }}</option>
                    <template v-if="datasetHeaders.length > 0" >
                        <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                    </template>
                </select>
            </div>
        </div>
        <div class="row mb-3">
            <div class="col-2">
                <input class="form-check-input" v-model="data.toTheResults" type="checkbox" id="toTheResults">
                <label class="form-check-label ps-2"  for="toTheResults">
                    {{ $t('toTheResults') }}
                </label>
            </div>
        </div>
        <div class="mb-3">
            <h4>{{ $t('parameterClasses') }}</h4>
            <table class="table">
                <thead>
                    <tr>
                        <th scope="col"></th>
                        <th scope="col">{{ $t('startClassification') }}</th>
                        <th scope="col">{{ $t('endClassification') }}</th>
                        <th scope="col">{{ $t('name') }}</th>
                        <th scope="col">{{ $t('color') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(parameterClass, i) in data.parameterClasses" class="align-middle"  :key="i">
                        <th scope="row"><i class="fa fa-remove" @click="removeParameterClass(i)"></i></th>
                        <td>
                            <input v-model="parameterClass.startClassification" :disabled="i > 0" type="number" class="form-control" :id="'startClassification_' + i">
                        </td>
                        <td>
                            <input v-model="parameterClass.endClassification" @input="updateParameterClassStart(parameterClass.endClassification, i)"  type="number" class="form-control" :id="'endClassification_' + i">
                        </td>
                        <td>
                            <input v-model="parameterClass.name" type="string" class="form-control" :id="'name_' + i">
                        </td>
                        <td>
                            <input v-model="parameterClass.color" type="color" class="form-control" :id="'color_' + i">
                        </td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-secondary w-25 mx-auto d-block" @click="addParameterClass"><i class="fa-solid fa-plus"></i></button>
        </div>
        <div class="d-grid gap-2 d-md-flex ">
            <template v-if="isUserAdmin">
                <button type="button" class="btn btn-primary" @click="onSubmit">{{ $t('save') }}</button>
                <button type="button" class="btn btn-danger" @click="showDeleteParameter">{{ $t('delete') }}</button>
            </template>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}/parameter/${this.$route.params.parameterId}` })">{{ $t('back') }}</button>
        </div>
        <ParameterDeleteModal v-if="showParameterDeleteModal" @reload="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })" @closeDeleteDialog="showParameterDeleteModal = false"/>
    </div>
</template>

<script>
import ParameterApi from "../../api/server/parameter";
import CurveApi from "../../api/server/curve";
import DatasetApi from "../../api/server/dataset";
import AssetTypeApi from "../../api/server/assetType";
import ParameterDeleteModal from '../parameters/ParameterDeleteModal.vue';
import ModelingApi from "../../api/server/modeling";
import { CheckIfUserIsEditor } from '../../authCache';

export default {
    name: "parameterdetail",
    watch: {
        $route: "requestData",
    },
    components: {
        ParameterDeleteModal,
    },
    mounted() {
        this.requestData();
    },
    methods: {
        async requestData() {
            this.pending = true;
            this.isUserAdmin = await CheckIfUserIsEditor();

            this.data = await ParameterApi.parameterDetail(this.$route.params.id, this.$route.params.parameterId);
            var assetType = await AssetTypeApi.assetTypeDetail(this.$route.params.id, this.$route.params.assetTypeId);
            this.assetTypeName = assetType.name;

            var evaluationList = await ParameterApi.evaluationList(this.$route.params.id, this.$route.params.parameterId);
            var curveList = await CurveApi.curveList(this.$route.params.id);
            
            var dataset = await DatasetApi.datasetDetail(this.$route.params.id, assetType.datasetId);
            var datasetContent = JSON.parse(dataset.content);
            this.datasetHeaders = datasetContent["headers"];

            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;


            if(evaluationList.length > 0) {
                this.parameterContainsEvaluation = true;
            }
            if(curveList.length > 0 && curveList.findIndex(c => c.parameterId == this.$route.params.parameterId) >= 0) {
                this.parameterContainsCurves = true;
            }

            for(var i in this.costFactors) {
                var attr = this.costFactors[i];
                if(this.datasetHeaders.findIndex(d => d.name == attr) < 0 && this.missingDatasetHeaders.findIndex(d => d.name == attr) < 0) {
                    this.missingDatasetHeaders.push({ name: attr, alias: attr });
                }
            }

            this.pending = false;
        },
        async onSubmit() {
            if(this.data.name.length > 0) {
                if(this.costFactors.length > 1)
                    this.data.costFactor = this.costFactors.join(";");
                else 
                    this.data.costFactor = this.costFactors.join("");
                
                await ParameterApi.parameterUpdate(this.$route.params.id, this.data);
                if(!this.isFormValid())
                    this.showWarning();
                else 
                    this.showSuccess();
            } else {
                this.showEmptyNameError();
            }
        },
        isFormValid() {
            for(var i in this.costFactors) {
                var attr = this.costFactors[i];
                if(this.datasetHeaders.findIndex(d => d.name == attr) < 0 && attr != "")
                    return false;
            }

            return true
        },
        removeParameterClass(rowIndex) {
            this.data.parameterClasses.splice(rowIndex, 1);
        },
        addParameterClass() {
            this.data.parameterClasses.push({  
                color: "#000000",
                name: "",
                startClassification: this.getMinStartParameterClass(),
                endClassification: 0.0
            })
        },
        updateParameterClassStart(value, i) {
            var range = this.data.parameterClasses[i+1];
            if(range) {
                range.startClassification = value;
            }
        },
        getMinStartParameterClass() {
            var minStartParameterClass = 0;
            if(this.data.parameterClasses.length > 0)
            minStartParameterClass = this.data.parameterClasses[this.data.parameterClasses.length -1].endClassification;

            return minStartParameterClass;
        },
        showDeleteParameter() {
            this.parameterToDeleteId = this.data.id;
            this.showParameterDeleteModal = true;
        }
    },
    notifications: {
        showSuccess: { 
            title: 'success',
            message: 'parameterUpdated',
            type: 'success'
        },
        showWarning: {
            title: 'warning',
            message: 'warningAttributeRemoved',
            type: 'warn'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    },
    data() {
        return {
            costFactors: [],
            missingDatasetHeaders: [],
            datasetHeaders: [],
            data: {
                name: "",
                id: ""
            },
            showParameterDeleteModal: false,
            parameterToDeleteId: 0,
            modelingName: null,
            assetTypeName: null,
            pending: true,
            isUserAdmin: false,
            parameterContainsEvaluation: false,
            parameterContainsCurves: false
        }
    }
};
</script>